import { Identifier } from 'src/types/utilityTypes';
import api from 'src/utils/ajax';
import { camelcaseKeys } from 'src/utils/camelize-keys';

import { Version } from './_types';

export type VersionHistoryItem = {
  itemId: Identifier;
  itemType: string;
  label?: string;
};

export type VersionWithSource = Version & {
  sourceLabel?: string;
};

export const fetchVersionHistory = async (
  itemType: string,
  itemId: Identifier
): Promise<Version[]> => {
  const response = await api.get('/api/v4/paper_trail', {
    params: {
      item_type: itemType,
      item_id: itemId,
    },
  });

  return response.data.map((version: any) =>
    camelcaseKeys(version)
  ) as Version[];
};

export const fetchMultipleVersionHistory = async (
  items: VersionHistoryItem[]
): Promise<VersionWithSource[]> => {
  const uniqueItems = items.filter(
    (item, index, arr) =>
      arr.findIndex(
        (other) =>
          other.itemType === item.itemType && other.itemId === item.itemId
      ) === index
  );

  const results = await Promise.all(
    uniqueItems.map(({ itemType, itemId }) =>
      fetchVersionHistory(itemType, itemId)
    )
  );

  const labelByKey = new Map(
    uniqueItems.map((item) => [`${item.itemType}:${item.itemId}`, item.label])
  );

  return results
    .flatMap((versions, index) => {
      const { itemType, itemId } = uniqueItems[index];
      const sourceLabel = labelByKey.get(`${itemType}:${itemId}`);

      return versions.map((version) => ({
        ...version,
        sourceLabel,
      }));
    })
    .sort((a, b) => {
      const createdAtDiff = Number(a.createdAt) - Number(b.createdAt);
      if (createdAtDiff !== 0) return createdAtDiff;
      return Number(a.id) - Number(b.id);
    });
};

export const versionQueryKeys = {
  versions: (itemType: string, itemId: Identifier) =>
    ['versions', itemType, itemId] as const,
  multipleVersions: (items: VersionHistoryItem[]) =>
    ['versions', 'multiple', items] as const,
} as const;
